/**
 * Basic device fingerprinting for session tracking
 * Note: This is intentionally simple. For production, consider using a library
 * like FingerprintJS for more robust device identification.
 */

export interface DeviceInfo {
  device_name: string;
  device_type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  browser: string;
  os: string;
  user_agent: string;
}

/**
 * Detect device type from user agent
 */
function detectDeviceType(ua: string): 'desktop' | 'mobile' | 'tablet' | 'unknown' {
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Detect browser from user agent
 */
function detectBrowser(ua: string): string {
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
}

/**
 * Detect OS from user agent
 */
function detectOS(ua: string): string {
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

/**
 * Get device information for session tracking
 */
export function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  const deviceType = detectDeviceType(ua);
  const browser = detectBrowser(ua);
  const os = detectOS(ua);
  const deviceName = `${browser} on ${os}`;

  return {
    device_name: deviceName,
    device_type: deviceType,
    browser,
    os,
    user_agent: ua,
  };
}

/**
 * Generate a simple device fingerprint hash
 * Note: This is not cryptographically secure and is only for basic tracking
 */
export function generateDeviceFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency,
  ].join('|');

  // Simple hash function (not cryptographically secure)
  let hash = 0;
  for (let i = 0; i < components.length; i++) {
    const char = components.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(36);
}
